/**
 * <HeroSkeletonReveal /> — "Connecting to AWS..." pre-reveal animation.
 *
 * v34.68 (Jun 3 2026) — Nova's anti-vanity fix (round 3):
 * "If the dashboard looks too polished, it triggers a 'marketing fluff' alarm.
 *  Show it CONNECTING, not just the finished screenshot."
 *
 * Wraps the HeroDashboardMockup. On first mount, plays a 3-step boot sequence
 * (~3 seconds total), then reveals the dashboard. Signals "live tool"
 * not "static screenshot."
 *
 * Plays ONCE per page-load (no replays on re-render).
 * Respects prefers-reduced-motion → reveal instantly.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, Check } from 'lucide-react';

const BOOT_STEPS = [
  { label: 'Connecting to AWS us-east-1...', durationMs: 900 },
  { label: 'Scanning 847 resources across 4 regions...', durationMs: 1100 },
  { label: 'Found 12 anomalies. Generating Auto-PR fixes...', durationMs: 1000 },
];

const HeroSkeletonReveal = ({ children }) => {
  const [step, setStep] = useState(0);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    // Respect reduced-motion → skip the boot animation entirely
    if (
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      setRevealed(true);
      return;
    }

    // Sequence the boot steps then reveal
    const timers = [];
    let accumulated = 0;
    BOOT_STEPS.forEach((s, i) => {
      accumulated += s.durationMs;
      timers.push(
        setTimeout(() => {
          if (i + 1 < BOOT_STEPS.length) {
            setStep(i + 1);
          } else {
            setRevealed(true);
          }
        }, accumulated)
      );
    });

    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="relative" data-testid="hero-skeleton-reveal">
      {/* The actual dashboard — visible after reveal */}
      <div
        className={`transition-opacity duration-700 ${revealed ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        {children}
      </div>

      {/* The boot-up overlay — visible until reveal */}
      {!revealed && (
        <div
          className="absolute inset-0 z-10 rounded-2xl border border-emerald-500/30 bg-slate-950 overflow-hidden"
          data-testid="hero-skeleton-overlay"
        >
          {/* mac chrome dots to match dashboard look */}
          <div className="px-4 py-3 border-b border-white/5 bg-slate-900/60 flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-rose-500/70" />
            <div className="w-3 h-3 rounded-full bg-amber-500/70" />
            <div className="w-3 h-3 rounded-full bg-emerald-500/70" />
            <div className="flex-1 mx-3 px-3 py-1 rounded-md bg-slate-950/60 border border-white/5 text-[11px] font-mono text-slate-400 truncate">
              app.cartieai.com/dashboard
            </div>
            <span className="text-[10px] font-mono text-emerald-300 uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30">
              live
            </span>
          </div>

          {/* boot log */}
          <div className="px-6 py-8 sm:px-8 sm:py-10">
            <div className="space-y-3 font-mono text-[13px]">
              {BOOT_STEPS.map((s, i) => {
                const isDone = i < step;
                const isActive = i === step;
                const isPending = i > step;
                return (
                  <div
                    key={i}
                    className={`flex items-center gap-3 transition-opacity duration-500 ${
                      isPending ? 'opacity-25' : 'opacity-100'
                    }`}
                    data-testid={`hero-skeleton-step-${i}`}
                  >
                    {isDone ? (
                      <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                    ) : isActive ? (
                      <Loader2 className="w-4 h-4 text-emerald-400 animate-spin shrink-0" />
                    ) : (
                      <div className="w-4 h-4 rounded-full border border-slate-700 shrink-0" />
                    )}
                    <span className={isActive ? 'text-emerald-300' : isDone ? 'text-slate-300' : 'text-slate-600'}>
                      {s.label}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* shimmering skeleton bars to hint at the dashboard underneath */}
            <div className="mt-8 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-14 rounded-lg bg-slate-900 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 bg-[length:200%_100%] animate-skeleton"
                    style={{ animationDelay: `${i * 150}ms` }}
                  />
                ))}
              </div>
              <div className="h-20 rounded-lg bg-slate-900 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 bg-[length:200%_100%] animate-skeleton" />
              <div className="h-10 rounded-lg bg-slate-900 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 bg-[length:200%_100%] animate-skeleton" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeroSkeletonReveal;
